import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { PageTitle } from '../styles/SharedStyles';
import { 
  AnalysisContainer, 
  ContentContainer, 
  SummaryContainer,
  TabContainer,
  TabList,
  TabButton,
  TabContent
} from '../styles/AnalysisStyles';
import { useAnalysis } from '../hooks/useAnalysis';
import SummaryTable from '../components/features/analysis/SummaryTable';
import IconSection from '../components/features/analysis/IconSection';
import IntelligenceTab from '../components/features/analysis/IntelligenceTab';
import ArtifactsTab from '../components/features/analysis/ArtifactsTab';
import ComponentModal from '../components/features/analysis/ComponentModal';
import CodePreviewModal from '../components/features/analysis/CodePreviewModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import DynamicAnalysisTab from '../components/features/analysis/DynamicAnalysisTab';
import CommentsSection from '../components/features/analysis/CommentsSection';
import { ComponentButtonsContainer, ComponentButton, ComponentName, ComponentCount } from '../styles/AnalysisStyles';
import { apiService } from '../services/api';



const Analysis = () => {
  const { sha256 } = useParams();
  const { 
    analysisData, 
    loading, 
    error, 
    isBookmarked, 
    canRescan,
    canDownload,
    isDownloading,
    handleDownload,
    toggleBookmark,
    handleRescan 
  } = useAnalysis(sha256);

  const [activeTab, setActiveTab] = useState('intelligence');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  useEffect(() => {
    if (!sha256) return;
    setCommentsLoading(true);
    apiService.getComments(sha256)
      .then(data => setComments(data.comments || []))
      .catch(() => {})
      .finally(() => setCommentsLoading(false));
  }, [sha256]);

  // Render loading state
  if (loading) {
    return (
      <AnalysisContainer>
        <Navbar />
        <ContentContainer>
          <LoadingSpinner>Loading analysis...</LoadingSpinner>
        </ContentContainer>
      </AnalysisContainer>
    );
  }

  // Render error state
  if (error) {
    return (
      <AnalysisContainer>
        <Navbar />
        <ContentContainer>
          <ErrorMessage>{error}</ErrorMessage>
        </ContentContainer>
      </AnalysisContainer>
    );
  }

  // Render main analysis content
  return (
    <AnalysisContainer>
      <Navbar />
      <ContentContainer>
        <PageTitle>APK Analysis</PageTitle>
        
        <SummaryContainer>
          <SummaryTable analysisData={analysisData} />
          <IconSection 
            analysisData={analysisData} 
            isBookmarked={isBookmarked}
            toggleBookmark={toggleBookmark}
            canRescan={canRescan}
            handleRescan={handleRescan}
            canDownload={canDownload}
            isDownloading={isDownloading}
            handleDownload={handleDownload}
          />
        </SummaryContainer>

        {/* Component buttons section */}
        <ComponentButtonsContainer>
          <ComponentButton onClick={() => setSelectedComponent('permissions')}>
            <ComponentName>Permissions</ComponentName>
            <ComponentCount>{analysisData?.permissions?.length || 0}</ComponentCount>
          </ComponentButton>
          
          <ComponentButton onClick={() => setSelectedComponent('services')}>
            <ComponentName>Services</ComponentName>
            <ComponentCount>{analysisData?.services?.length || 0}</ComponentCount>
          </ComponentButton>
          
          <ComponentButton onClick={() => setSelectedComponent('activities')}>
            <ComponentName>Activities</ComponentName>
            <ComponentCount>{analysisData?.activities?.length || 0}</ComponentCount>
          </ComponentButton>
          
          <ComponentButton onClick={() => setSelectedComponent('receivers')}>
            <ComponentName>Receivers</ComponentName>
            <ComponentCount>{analysisData?.receivers?.length || 0}</ComponentCount>
          </ComponentButton>
        </ComponentButtonsContainer>

        {/* Tab Container */}
        <TabContainer>
          <TabList>
            <TabButton 
              active={activeTab === 'intelligence'} 
              onClick={() => setActiveTab('intelligence')}
            >
              Intelligence
            </TabButton>

            <TabButton 
              active={activeTab === 'artifacts'} 
              onClick={() => setActiveTab('artifacts')}
            >
              Artifacts ({analysisData?.artifacts?.length || 0})
            </TabButton>

            <TabButton 
              active={activeTab === 'dynamic'} 
              onClick={() => setActiveTab('dynamic')}
            >
              Dynamic Information
            </TabButton>
            
            <TabButton 
              active={activeTab === 'comments'}
              onClick={() => setActiveTab('comments')}
            >
              Comments
            </TabButton>
          </TabList>

          {/* Intelligence Tab */}
          <TabContent active={activeTab === 'intelligence'}>
            <IntelligenceTab 
              analysisData={analysisData} 
              onCodeView={(code) => setSelectedCode(code)}
            />
          </TabContent>

          {/* Artifacts Tab */}
          <TabContent active={activeTab === 'artifacts'}>
            <ArtifactsTab 
              analysisData={analysisData}
              sha256={sha256}
            />
          </TabContent>

          {/* Dynamic Analysis Tab */}
          <TabContent active={activeTab === 'dynamic'}>
            <div style={{ padding: '2rem' }}>
              <DynamicAnalysisTab sha256={sha256} />
            </div>
          </TabContent>

          {/* Comments Tab */}
          <TabContent active={activeTab === 'comments'}>
            <CommentsSection
              sha256={sha256}
              comments={comments}
              commentsLoading={commentsLoading}
              onCommentAdded={(c) => setComments(prev => [c, ...prev])}
            />
          </TabContent>
        </TabContainer>

        {/* Modals */}
        <ComponentModal
          selectedComponent={selectedComponent}
          analysisData={analysisData}
          onClose={() => setSelectedComponent(null)}
        />

        <CodePreviewModal
          selectedCode={selectedCode}
          onClose={() => setSelectedCode(null)}
        />

      </ContentContainer>
    </AnalysisContainer>
  );
};

export default Analysis;